import React, { useState, useRef, useEffect } from 'react';
import { uploadReferenceImage } from '../../api/client';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { formatInputNumber } from '../../utils/formatting';

const ReferenceImagePanel: React.FC = () => {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const updateProjectReferenceImage = useSceneStore((state: SceneState) => state.updateProjectReferenceImage);
  const referenceImageVisible = useSceneStore((state: SceneState) => state.referenceImageVisible);
  const setReferenceImageVisible = useSceneStore((state: SceneState) => state.setReferenceImageVisible);
  
  // Wall drawing state
  const isWallDrawingMode = useSceneStore((state: SceneState) => state.isWallDrawingMode);
  const currentWallPoints = useSceneStore((state: SceneState) => state.currentWallPoints);
  const wallThickness = useSceneStore((state: SceneState) => state.wallThickness);
  const setWallThickness = useSceneStore((state: SceneState) => state.setWallThickness);
  const wallHeight = useSceneStore((state: SceneState) => state.wallHeight);
  const setWallHeight = useSceneStore((state: SceneState) => state.setWallHeight);
  const finishCurrentWall = useSceneStore((state: SceneState) => state.finishCurrentWall);
  const clearCurrentWall = useSceneStore((state: SceneState) => state.clearCurrentWall);

  useEffect(() => {
    if (currentProject?.reference_image_url) {
      const original = currentProject.reference_image_original_filename;
      if (original && original.trim().length > 0) {
        setUploadedFileName(original);
      } else {
        const url = currentProject.reference_image_url;
        const fileName = url.substring(url.lastIndexOf('/') + 1);
        setUploadedFileName(fileName);
      }
    } else {
      setUploadedFileName(null);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [currentProject]);

  const handleUpload = async (file: File) => {
    if (!currentProject) {
      alert('Please select a project first.');
      return;
    }

    try {
      const updatedProject = await uploadReferenceImage(currentProject.id, file);
      if (updatedProject.reference_image_url) {
        updateProjectReferenceImage(
          updatedProject.reference_image_url,
          updatedProject.reference_image_original_filename
        );
        useSceneStore.getState().setIsReferenceImageAlignMode(true);
        useSceneStore.getState().setReferenceImageAlignMethod('interactive');
        useSceneStore.getState().setReferenceImageAlignMode('translate');
        useSceneStore.getState().setSelectedObjectId('reference-image');
        setUploadedFileName(updatedProject.reference_image_original_filename || file.name);
      }
    } catch (error) {
      console.error('Error uploading reference image:', error);
      alert('Failed to upload reference image.');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      handleUpload(file);
    }
  };

  const panelStyle: React.CSSProperties = {
    background: 'rgba(25, 35, 45, 0.8)',
    color: 'white',
    padding: '15px',
    borderRadius: '8px',
    flex: '0 0 auto',
    minHeight: 0,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: '#334155',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '4px',
    padding: '4px 8px',
    marginBottom: '10px',
  };

  const buttonStyle: React.CSSProperties = {
    background: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    width: '100%',
  };

  return (
    <div style={panelStyle}>
      {!isWallDrawingMode && <h4>Reference Image</h4>}
      
      {/* Reference Image Controls - Hidden in wall drawing mode */}
      {!isWallDrawingMode && (
        <>
          <input data-testid="upload-file" ref={inputRef} type="file" accept="image/*,.pdf" onChange={handleFileChange} style={inputStyle} />
          {uploadedFileName && <p style={{ marginTop: '-10px', marginBottom: '10px' }}>{uploadedFileName}</p>}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                useSceneStore.getState().setIsReferenceImageAlignMode(true);
                useSceneStore.getState().setReferenceImageAlignMethod('interactive');
                useSceneStore.getState().setReferenceImageAlignMode('translate');
                useSceneStore.getState().setSelectedObjectId('reference-image');
              }}
              disabled={!currentProject?.reference_image_url}
              style={{ ...buttonStyle, width: 'auto', flex: 1 }}
            >
              Configure
            </button>
            <button
              onClick={() => setReferenceImageVisible(!referenceImageVisible)}
              disabled={!currentProject?.reference_image_url}
              style={{ ...buttonStyle, width: 'auto', flex: 1 }}
            >
              {referenceImageVisible ? 'Hide' : 'Show'}
            </button>
          </div>
        </>
      )}
      
      {/* Wall Drawing Requirement Message */}
      {!currentProject?.reference_image_url && (
        <div style={{ 
          marginTop: '15px', 
          padding: '12px', 
          background: 'rgba(59, 130, 246, 0.1)', 
          border: '1px solid rgba(59, 130, 246, 0.3)', 
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '12px', 
            color: '#93c5fd',
            lineHeight: '1.4'
          }}>
            📐 <strong>Wall Drawing Available</strong><br/>
            Upload and configure a reference image to enable wall drawing mode.
          </p>
        </div>
      )}
      
      {/* Wall Drawing Controls */}
      {currentProject?.reference_image_url && referenceImageVisible && (
        <>
          <div style={{ marginTop: '15px', paddingTop: '15px' }}>
            <h5 style={{ margin: '0 0 10px 0', color: '#e2e8f0' }}>
              {isWallDrawingMode ? 'Wall Drawing' : ''}
            </h5>
            
            {isWallDrawingMode && (
              <>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Thickness (m):</label>
                    <input
                      type="number"
                      min="0.1"
                      max="2.0"
                      step="0.001"
                      value={formatInputNumber(wallThickness)}
                      onChange={(e) => setWallThickness(parseFloat(e.target.value) || 0.2)}
                      style={{ ...inputStyle, fontSize: '12px', padding: '4px 6px' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Height (m):</label>
                    <input
                      type="number"
                      min="1.0"
                      max="10.0"
                      step="0.001"
                      value={formatInputNumber(wallHeight)}
                      onChange={(e) => setWallHeight(parseFloat(e.target.value) || 2.5)}
                      style={{ ...inputStyle, fontSize: '12px', padding: '4px 6px' }}
                    />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <button
                    onClick={finishCurrentWall}
                    disabled={currentWallPoints.length < 2}
                    style={{ ...buttonStyle, width: 'auto', flex: 1, fontSize: '12px', padding: '6px 8px' }}
                  >
                    Finish Wall ({currentWallPoints.length} pts)
                  </button>
                  <button
                    onClick={clearCurrentWall}
                    disabled={currentWallPoints.length === 0}
                    style={{ ...buttonStyle, width: 'auto', flex: 1, fontSize: '12px', padding: '6px 8px' }}
                  >
                    Clear Current
                  </button>
                </div>
                
                <p style={{ color: '#10b981', fontSize: '12px', margin: '5px 0 0 0' }}>
                  Click on the reference image to draw wall points. Press Enter, double-click, or middle-click to finish, press Esc to clear.
                </p>
              </>
            )}
          </div>
        </>
      )}
      
      {/* Modal removed as it's replaced by ReferenceImageAlignmentPanel */}
    </div>
  );
};

export default ReferenceImagePanel;
